import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { atomDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

const GettingStartedPage = () => {
  const { t } = useTranslation();
  return (
    <div className="bg-white">
      {/* Hero Banner */}
      <div className="bg-gradient-to-r from-dblift-blue-dark to-dblift-blue-light py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <motion.h1 
              className="text-4xl md:text-5xl font-bold text-white mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {t('gettingStarted.title').split(' ').slice(0, -1).join(' ')} <span className="text-dblift-orange">{t('gettingStarted.title').split(' ').pop()}</span>
            </motion.h1>
            <motion.p 
              className="text-xl text-white opacity-90 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              {t('gettingStarted.description')}
            </motion.p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-16">
              
              {/* Introduction */}
              <section>
                <h2 className="text-3xl font-bold text-dblift-blue-dark mb-6">{t('gettingStarted.intro.title')}</h2>
                <p className="text-lg mb-4">
                  {t('gettingStarted.intro.description')}
                </p>
                <p className="text-lg mb-4">
                  {t('gettingStarted.intro.features')}
                </p>
                <ul className="list-disc pl-6 space-y-2 mb-6">
                  <li>{t('gettingStarted.intro.featuresList.0')}</li>
                  <li>{t('gettingStarted.intro.featuresList.1')}</li>
                  <li>{t('gettingStarted.intro.featuresList.2')}</li>
                  <li>{t('gettingStarted.intro.featuresList.3')}</li>
                  <li>{t('gettingStarted.intro.featuresList.4')}</li>
                </ul>
              </section>

              {/* Installation */}
              <section id="installation">
                <h2 className="text-3xl font-bold text-dblift-blue-dark mb-6">{t('gettingStarted.installation.title')}</h2>
                <p className="text-lg mb-4">
                  {t('gettingStarted.installation.description')}
                </p>
                
                <div className="bg-dblift-gray-light rounded-lg p-6 mb-6">
                  <h3 className="text-xl font-semibold text-dblift-blue-dark mb-4">{t('gettingStarted.installation.viaPip')}</h3>
                  <SyntaxHighlighter language="bash" style={atomDark} className="rounded-md mb-4">
                    pip install dblift
                  </SyntaxHighlighter>
                  <p className="text-sm text-gray-600">
                    {t('gettingStarted.installation.requires')}
                  </p>
                </div>

                <div className="bg-dblift-gray-light rounded-lg p-6 mb-6">
                  <h3 className="text-xl font-semibold text-dblift-blue-dark mb-4">{t('gettingStarted.installation.directDownload')}</h3>
                  <p className="mb-4">
                    {t('gettingStarted.installation.downloadDesc')}
                  </p>
                  <div className="text-center">
                    <Link to="/download" className="btn btn-primary">
                      {t('docs.download')}
                    </Link>
                  </div>
                </div>

                <p className="text-lg mt-4">
                  {t('gettingStarted.installation.verifyInstall')}
                </p>
                <SyntaxHighlighter language="bash" style={atomDark} className="rounded-md mt-2">
                  dblift info
                </SyntaxHighlighter>
              </section>

              {/* Configuration */}
              <section id="configuration">
                <h2 className="text-3xl font-bold text-dblift-blue-dark mb-6">{t('gettingStarted.configuration.title')}</h2>
                <p className="text-lg mb-4">
                  {t('gettingStarted.configuration.description')}
                </p>
                
                <div className="bg-dblift-gray-light rounded-lg p-6 mb-6">
                  <h3 className="text-xl font-semibold text-dblift-blue-dark mb-4">{t('gettingStarted.configuration.minimal')}</h3>
                  <SyntaxHighlighter language="yaml" style={atomDark} className="rounded-md">
{`database:
  url: jdbc:oracle:thin:@localhost:1521:XE
  username: myuser
  password: mypassword
  schema: myschema

migrations_dir: ./migrations
jdbc_driver_path: ./drivers/ojdbc8.jar`}
                  </SyntaxHighlighter>
                </div>

                <div className="bg-dblift-gray-light rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-dblift-blue-dark mb-4">{t('gettingStarted.configuration.complete')}</h3>
                  <SyntaxHighlighter language="yaml" style={atomDark} className="rounded-md">
{`database:
  # URL de connexion JDBC
  url: jdbc:oracle:thin:@localhost:1521:XE
  username: myuser
  password: mypassword
  schema: myschema

  # Options avancées (optionnelles)
  connection_timeout: 30
  idle_timeout: 600
  max_pool_size: 10

# Répertoire contenant les scripts de migration
migrations_dir: ./migrations

# Options de journalisation (optionnelles)
logging:
  level: INFO
  format: html
  output: ./logs/migrations.html

# Options de comportement (optionnelles)
options:
  baseline_on_migrate: true
  clean_on_validation_error: false
  out_of_order: false
  validate_on_migrate: true
  mixed_migrations: false
  
# Gestion des hooks (optionnelle)
hooks:
  before_migrate: ./scripts/pre_migration.sh
  after_migrate: ./scripts/post_migration.sh`}
                  </SyntaxHighlighter>
                </div>

                <div className="mt-6">
                  <h3 className="text-xl font-semibold text-dblift-blue-dark mb-4">{t('gettingStarted.configuration.connectionUrls')}</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-dblift-blue-dark text-white">
                          <th className="px-4 py-2 text-left">{t('gettingStarted.configuration.dbms')}</th>
                          <th className="px-4 py-2 text-left">{t('gettingStarted.configuration.urlFormat')}</th>
                          <th className="px-4 py-2 text-left">{t('gettingStarted.configuration.status')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="bg-white border-b border-gray-200">
                          <td className="px-4 py-2 font-semibold">Oracle</td>
                          <td className="px-4 py-2 font-mono text-sm">jdbc:oracle:thin:@host:port:SID</td>
                          <td className="px-4 py-2"><span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-semibold">{t('gettingStarted.configuration.supported')}</span></td>
                        </tr>
                        <tr className="bg-dblift-gray-light border-b border-gray-200">
                          <td className="px-4 py-2 font-semibold">SQL Server</td>
                          <td className="px-4 py-2 font-mono text-sm">jdbc:sqlserver://host:port;databaseName=database</td>
                          <td className="px-4 py-2"><span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-semibold">{t('gettingStarted.configuration.supported')}</span></td>
                        </tr>
                        <tr className="bg-white border-b border-gray-200">
                          <td className="px-4 py-2 font-semibold">DB2</td>
                          <td className="px-4 py-2 font-mono text-sm">jdbc:db2://host:port/database</td>
                          <td className="px-4 py-2"><span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-semibold">{t('gettingStarted.configuration.supported')}</span></td>
                        </tr>
                        <tr className="bg-dblift-gray-light border-b border-gray-200">
                          <td className="px-4 py-2 font-semibold">PostgreSQL</td>
                          <td className="px-4 py-2 font-mono text-sm">jdbc:postgresql://host:port/database</td>
                          <td className="px-4 py-2"><span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-semibold">{t('gettingStarted.configuration.planned')}</span></td>
                        </tr>
                        <tr className="bg-white border-b border-gray-200">
                          <td className="px-4 py-2 font-semibold">MySQL</td>
                          <td className="px-4 py-2 font-mono text-sm">jdbc:mysql://host:port/database</td>
                          <td className="px-4 py-2"><span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-semibold">{t('gettingStarted.configuration.planned')}</span></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>

              {/* Creating Migration Scripts */}
              <section id="scripts">
                <h2 className="text-3xl font-bold text-dblift-blue-dark mb-6">{t('gettingStarted.scripts.title')}</h2>
                <p className="text-lg mb-4">
                  {t('gettingStarted.scripts.description')}
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div className="bg-dblift-gray-light rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-dblift-blue-dark mb-4">{t('gettingStarted.scripts.versionedTitle')}</h3>
                    <p className="mb-2">{t('gettingStarted.scripts.format')} <code>V{'version'}__{'description'}.sql</code></p>
                    <p className="mb-4">{t('gettingStarted.scripts.example')} <code>V1_0_0__Create_users_table.sql</code></p>
                    <SyntaxHighlighter language="sql" style={atomDark} className="rounded-md">
{`-- Create users table
CREATE TABLE users (
  id SERIAL PRIMARY KEY,
  username VARCHAR(100) NOT NULL,
  email VARCHAR(255) NOT NULL,
  created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP
);

-- Add unique constraints
ALTER TABLE users ADD CONSTRAINT uq_users_username UNIQUE (username);
ALTER TABLE users ADD CONSTRAINT uq_users_email UNIQUE (email);`}
                    </SyntaxHighlighter>
                  </div>
                  
                  <div className="bg-dblift-gray-light rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-dblift-blue-dark mb-4">{t('gettingStarted.scripts.repeatableTitle')}</h3>
                    <p className="mb-2">{t('gettingStarted.scripts.format')} <code>R__{'description'}.sql</code></p>
                    <p className="mb-4">{t('gettingStarted.scripts.example')} <code>R__Create_active_users_view.sql</code></p>
                    <SyntaxHighlighter language="sql" style={atomDark} className="rounded-md">
{`-- Create or replace view for active users
CREATE OR REPLACE VIEW active_users AS
SELECT id, username, email, created_at
FROM users
WHERE active = true;`}
                    </SyntaxHighlighter>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-dblift-gray-light rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-dblift-blue-dark mb-4">{t('gettingStarted.scripts.undoTitle')}</h3>
                    <p className="mb-2">{t('gettingStarted.scripts.format')} <code>U{'version'}__{'description'}.sql</code></p>
                    <p className="mb-4">{t('gettingStarted.scripts.example')} <code>U1_0_0__Drop_users_table.sql</code></p>
                    <SyntaxHighlighter language="sql" style={atomDark} className="rounded-md">
{`-- Drop users table
DROP TABLE IF EXISTS users;`}
                    </SyntaxHighlighter>
                  </div>
                  
                  <div className="bg-dblift-gray-light rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-dblift-blue-dark mb-4">{t('gettingStarted.scripts.tagsTitle')}</h3>
                    <p className="mb-2">{t('gettingStarted.scripts.format')} <code>V{'version'}__{'description'}[tag1,tag2].sql</code></p>
                    <p className="mb-4">{t('gettingStarted.scripts.example')} <code>V1_0_1__Add_user_indexes[perf,index].sql</code></p>
                    <SyntaxHighlighter language="sql" style={atomDark} className="rounded-md">
{`-- Add performance indexes
CREATE INDEX idx_users_email ON users(email);
CREATE INDEX idx_users_created_at ON users(created_at);`}
                    </SyntaxHighlighter>
                  </div>
                </div>
              </section>

              {/* Running Migrations */}
              <section id="running">
                <h2 className="text-3xl font-bold text-dblift-blue-dark mb-6">{t('gettingStarted.running.title')}</h2>
                <p className="text-lg mb-6">
                  {t('gettingStarted.running.description')}
                </p>
                
                <div className="bg-dblift-gray-light rounded-lg p-6 mb-6">
                  <h3 className="text-xl font-semibold text-dblift-blue-dark mb-4">{t('gettingStarted.running.basicTitle')}</h3>
                  <SyntaxHighlighter language="bash" style={atomDark} className="rounded-md mb-2">
                    dblift migrate
                  </SyntaxHighlighter>
                  <p>
                    {t('gettingStarted.running.basicDesc')}
                  </p>
                </div>
                
                <div className="bg-dblift-gray-light rounded-lg p-6 mb-6">
                  <h3 className="text-xl font-semibold text-dblift-blue-dark mb-4">{t('gettingStarted.running.optionsTitle')}</h3>
                  <SyntaxHighlighter language="bash" style={atomDark} className="rounded-md mb-4">
{`${t('commandExamples.migrate.toVersion')}
dblift migrate --target-version=1.2.0

${t('commandExamples.migrate.withTags')}
dblift migrate --tags=perf,prod

${t('commandExamples.migrate.dryRun')}
dblift migrate --dry-run

${t('commandExamples.migrate.withConfig')}
dblift migrate --config=prod-dblift.yaml`}
                  </SyntaxHighlighter>
                </div>

                <div className="bg-dblift-gray-light rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-dblift-blue-dark mb-4">{t('gettingStarted.running.checkTitle')}</h3>
                  <SyntaxHighlighter language="bash" style={atomDark} className="rounded-md mb-2">
                    dblift info
                  </SyntaxHighlighter>
                  <p className="mb-4">
                    {t('gettingStarted.running.checkDesc')}
                  </p>
                  
                  <h4 className="text-lg font-semibold text-dblift-blue-dark mb-2">{t('gettingStarted.running.outputTitle')}</h4>
                  <SyntaxHighlighter language="text" style={atomDark} className="rounded-md">
{`DBLift 2.1.0

Database: PostgreSQL 14.1
Schema: public
Schema version: 1.2.0

Applied Migrations:
┌────────────┬─────────────────────────────┬───────────┬────────────────────┐
│ Version    │ Description                 │ State     │ Installed On       │
├────────────┼─────────────────────────────┼───────────┼────────────────────┤
│ 1.0.0      │ Create users table          │ Success   │ 2023-06-15 10:23:42│
│ 1.1.0      │ Add roles table             │ Success   │ 2023-06-15 10:23:43│
│ 1.2.0      │ Create user_roles table     │ Success   │ 2023-06-15 10:23:44│
│ << Repeatable >> │ Create active_users view    │ Success   │ 2023-06-15 10:23:45│
└────────────┴─────────────────────────────┴───────────┴────────────────────┘

Pending Migrations:
┌────────────┬─────────────────────────────┬───────────────┐
│ Version    │ Description                 │ Tags          │
├────────────┼─────────────────────────────┼───────────────┤
│ 1.3.0      │ Add user preferences        │ [feature]     │
│ 1.4.0      │ Add email verification      │ [security]    │
└────────────┴─────────────────────────────┴───────────────┘`}
                  </SyntaxHighlighter>
                </div>
              </section>

              {/* Advanced Features */}
              <section id="advanced">
                <h2 className="text-3xl font-bold text-dblift-blue-dark mb-6">{t('gettingStarted.advanced.title')}</h2>
                
                <div className="space-y-6">
                  <div className="bg-dblift-gray-light rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-dblift-blue-dark mb-4">{t('gettingStarted.advanced.undoTitle')}</h3>
                    <p className="mb-4">
                      {t('gettingStarted.advanced.undoDesc')}
                    </p>
                    <SyntaxHighlighter language="bash" style={atomDark} className="rounded-md">
{`${t('commandExamples.undo.last')}
dblift undo

${t('commandExamples.undo.toVersion')}
dblift undo --target-version=1.1.0`}
                    </SyntaxHighlighter>
                  </div>
                  
                  <div className="bg-dblift-gray-light rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-dblift-blue-dark mb-4">{t('gettingStarted.advanced.validateTitle')}</h3>
                    <p className="mb-4">
                      {t('gettingStarted.advanced.validateDesc')}
                    </p>
                    <SyntaxHighlighter language="bash" style={atomDark} className="rounded-md">
                      dblift validate
                    </SyntaxHighlighter>
                  </div>
                  
                  <div className="bg-dblift-gray-light rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-dblift-blue-dark mb-4">{t('gettingStarted.advanced.baselineTitle')}</h3>
                    <p className="mb-4">
                      {t('gettingStarted.advanced.baselineDesc')}
                    </p>
                    <SyntaxHighlighter language="bash" style={atomDark} className="rounded-md">
{`${t('commandExamples.baseline.setBaseline')}
dblift baseline --baseline-version=1.0.0

${t('commandExamples.baseline.withDescription')}
dblift baseline --baseline-version=1.0.0 --baseline-description="Initial schema"`}
                    </SyntaxHighlighter>
                  </div>
                  
                  <div className="bg-dblift-gray-light rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-dblift-blue-dark mb-4">{t('gettingStarted.advanced.repairTitle')}</h3>
                    <p className="mb-4">
                      {t('gettingStarted.advanced.repairDesc')}
                    </p>
                    <SyntaxHighlighter language="bash" style={atomDark} className="rounded-md">
                      dblift repair
                    </SyntaxHighlighter>
                  </div>
                </div>
              </section>

              {/* Best Practices */}
              <section id="best-practices">
                <h2 className="text-3xl font-bold text-dblift-blue-dark mb-6">{t('gettingStarted.bestPractices.title')}</h2>
                
                <div className="bg-dblift-gray-light rounded-lg p-6 mb-6">
                  <ul className="space-y-4">
                    <li className="flex items-start">
                      <span className="text-dblift-orange font-bold text-xl mr-2">•</span>
                      <div>
                        <p className="font-semibold">{t('gettingStarted.bestPractices.semantic')}</p>
                        <p>{t('gettingStarted.bestPractices.semanticDesc')}</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <span className="text-dblift-orange font-bold text-xl mr-2">•</span>
                      <div>
                        <p className="font-semibold">{t('gettingStarted.bestPractices.oneChange')}</p>
                        <p>{t('gettingStarted.bestPractices.oneChangeDesc')}</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <span className="text-dblift-orange font-bold text-xl mr-2">•</span>
                      <div>
                        <p className="font-semibold">{t('gettingStarted.bestPractices.descriptions')}</p>
                        <p>{t('gettingStarted.bestPractices.descriptionsDesc')}</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <span className="text-dblift-orange font-bold text-xl mr-2">•</span>
                      <div>
                        <p className="font-semibold">{t('gettingStarted.bestPractices.tags')}</p>
                        <p>{t('gettingStarted.bestPractices.tagsDesc')}</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <span className="text-dblift-orange font-bold text-xl mr-2">•</span>
                      <div>
                        <p className="font-semibold">{t('gettingStarted.bestPractices.dryRun')}</p>
                        <p>{t('gettingStarted.bestPractices.dryRunDesc')}</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <span className="text-dblift-orange font-bold text-xl mr-2">•</span>
                      <div>
                        <p className="font-semibold">{t('gettingStarted.bestPractices.environments')}</p>
                        <p>{t('gettingStarted.bestPractices.environmentsDesc')}</p>
                      </div>
                    </li>
                  </ul>
                </div>
              </section>
            </div>

            {/* Next Steps */}
            <div className="mt-16 bg-dblift-blue-dark text-white rounded-lg p-8">
              <h2 className="text-3xl font-bold mb-6">{t('gettingStarted.nextSteps.title')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Link to="/docs" className="bg-white bg-opacity-10 hover:bg-opacity-20 p-6 rounded-lg transition-all duration-200">
                  <h3 className="text-xl font-semibold mb-2">{t('gettingStarted.nextSteps.docs')}</h3>
                  <p>{t('gettingStarted.nextSteps.docsDesc')}</p>
                </Link>
                <Link to="/features" className="bg-white bg-opacity-10 hover:bg-opacity-20 p-6 rounded-lg transition-all duration-200">
                  <h3 className="text-xl font-semibold mb-2">{t('gettingStarted.nextSteps.features')}</h3>
                  <p>{t('gettingStarted.nextSteps.featuresDesc')}</p>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GettingStartedPage;